
import React, { useState, useEffect, useRef, FC, useMemo } from 'react';
import { UserProfile, StudentConnection, Kudo, ReactionType, AcademicYear, Organization, KudoBadge, ClassRoom, Homework, StudentHomeworkStatus, TutorLog } from '../types';
import { useTranslate } from '../hooks/useTranslate';
import { KUDO_BADGES, TEACHER_KUDO_BADGES, KUDO_CATEGORY_COLORS, PRINCIPAL_AWARD_COLOR, REACTIONS } from '../lib/constants';
import { Avatar, VoiceInput, Header } from './common';
import { translateContent } from '../lib/gemini';
import { BRAIN_POWER_BADGES, computeStudentBrainPowerProfile, BrainPowerBadge } from '../lib/brainPowerUtils';

const ShareKudoModal = ({ kudo, student, org, onClose, t }: {
    kudo: Kudo;
    student: StudentConnection;
    org: Organization | undefined;
    onClose: () => void;
    t: (key: string, reps?: any) => string;
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [includeNote, setIncludeNote] = useState(true);
    const [canShare, setCanShare] = useState(false);
    const [isDrawing, setIsDrawing] = useState(true);
    const [shareBlob, setShareBlob] = useState<Blob | null>(null);

    useEffect(() => {
        try {
            const dummyFile = new File(['dummy'], 'dummy.png', { type: 'image/png' });
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [dummyFile] })) {
                setCanShare(true);
            }
        } catch (e) {
            console.error("Could not check for file sharing capabilities.", e);
            setCanShare(false);
        }
    }, []);

    const wrapText = (context: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number) => {
        const words = text.split(' ');
        let line = '';
        for(let n = 0; n < words.length; n++) {
            const testLine = line + words[n] + ' ';
            const metrics = context.measureText(testLine);
            const testWidth = metrics.width;
            if (testWidth > maxWidth && n > 0) {
                context.fillText(line, x, y);
                line = words[n] + ' ';
                y += lineHeight;
            } else {
                line = testLine;
            }
        }
        context.fillText(line, x, y);
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const badge = KUDO_BADGES.concat(TEACHER_KUDO_BADGES, BRAIN_POWER_BADGES as any).find(b => b.id === kudo.badgeId);
        if (!badge) return;

        const render = async () => {
            setIsDrawing(true);

            // 1. Ensure fonts are loaded before any drawing occurs.
            try {
                await document.fonts.load('100px "Material Icons Round"');
                await document.fonts.load('bold 48px Inter, sans-serif');
            } catch (e) {
                console.error("Font loading failed, certificate may not render correctly.", e);
            }

            // 2. Fetch logo image to prevent CORS blocking and canvas tainting.
            let logoImage: HTMLImageElement | null = null;
            if (org?.photoUrl) {
                try {
                    if (org.photoUrl.startsWith('data:')) {
                        logoImage = await new Promise((resolve, reject) => {
                            const img = new Image();
                            img.onload = () => resolve(img);
                            img.onerror = () => reject(new Error("Image load error"));
                            img.src = org.photoUrl!;
                        });
                    } else {
                        // Remote URL (e.g., Firebase Storage): route via same-origin server proxy to prevent CORS blocks
                        const isRemote = org.photoUrl.startsWith('http://') || org.photoUrl.startsWith('https://');
                        const fetchUrl = isRemote 
                            ? `/api/proxy-image?url=${encodeURIComponent(org.photoUrl)}`
                            : org.photoUrl;

                        let response: Response;
                        try {
                            response = await fetch(fetchUrl);
                            if (!response.ok) throw new Error(`Proxy error: ${response.status}`);
                        } catch (proxyErr) {
                            // If proxy fails or is unavailable, attempt direct fetch with cors
                            response = await fetch(org.photoUrl, { mode: 'cors' });
                        }

                        if (response.ok) {
                            const blob = await response.blob();
                            const objectURL = URL.createObjectURL(blob);
                            logoImage = await new Promise((resolve, reject) => {
                                const img = new Image();
                                img.onload = () => {
                                    URL.revokeObjectURL(objectURL);
                                    resolve(img);
                                };
                                img.onerror = () => {
                                    URL.revokeObjectURL(objectURL);
                                    reject(new Error("Image load error"));
                                };
                                img.src = objectURL;
                            });
                        }
                    }
                } catch (e) {
                    console.warn("Could not load organization logo for certificate, continuing with title typography only.", e);
                }
            }

            // --- START DRAWING ---
            
            // Clear canvas
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            const isBrainBadge = badge.category === 'brain_power' || badge.id.startsWith('brain-');

            // Draw Borders
            ctx.strokeStyle = isBrainBadge ? '#b45309' : '#4338ca'; // Amber-700 or Indigo-700
            ctx.lineWidth = 20;
            ctx.strokeRect(10, 10, canvas.width - 20, canvas.height - 20);

            ctx.strokeStyle = isBrainBadge ? '#fef3c7' : '#c7d2fe'; // Amber-100 or Indigo-200
            ctx.lineWidth = 4;
            ctx.strokeRect(25, 25, canvas.width - 50, canvas.height - 50);

            ctx.textAlign = 'center';
            const padding = 60;
            let yPos = padding;

            // 1. Logo
            if (logoImage) {
                yPos += 40;
                ctx.drawImage(logoImage, canvas.width / 2 - 40, yPos, 80, 80);
                yPos += 80 + 20;
            } else {
                yPos += 50;
            }

            // 2. School Name
            if (org?.name) {
                ctx.fillStyle = '#4B5563';
                ctx.font = 'bold 24px Inter, sans-serif';
                ctx.fillText(org.name, canvas.width / 2, yPos);
                yPos += 30;
            }

            // 3. Title
            ctx.fillStyle = isBrainBadge ? '#92400e' : '#374151';
            ctx.font = 'bold 30px Inter, sans-serif';
            ctx.fillText(isBrainBadge ? 'CERTIFICATE OF INDEPENDENT THINKING' : t('certificate_of_achievement'), canvas.width / 2, yPos);
            yPos += 50;

            // 4. "Awarded to"
            ctx.fillStyle = '#6B7280';
            ctx.font = '20px Inter, sans-serif';
            ctx.fillText(t('awarded_to'), canvas.width / 2, yPos);
            yPos += 50;

            // 5. Student Name
            ctx.fillStyle = '#111827';
            ctx.font = 'bold 48px Inter, sans-serif';
            ctx.fillText(student.childName, canvas.width / 2, yPos);
            yPos += 100;

            // 6. Badge Icon and Name
            ctx.font = '100px "Material Icons Round"';
            const categoryColor = KUDO_CATEGORY_COLORS[badge.category]?.hex || '#6366F1';
            ctx.fillStyle = kudo.isPrincipalAward ? PRINCIPAL_AWARD_COLOR.hex : categoryColor;
            ctx.fillText(badge.icon, canvas.width / 2, yPos);
            yPos += 70;
            
            ctx.fillStyle = '#111827';
            ctx.font = 'bold 36px Inter, sans-serif';
            ctx.fillText(badge.name, canvas.width / 2, yPos);
            yPos += 30;
            
            if (isBrainBadge && (badge as any).explicitSubtitle) {
                ctx.fillStyle = '#b45309';
                ctx.font = 'bold 17px Inter, sans-serif';
                ctx.fillText(`★ ${(badge as any).explicitSubtitle} ★`, canvas.width / 2, yPos);
                yPos += 28;
            }

            if (kudo.isPrincipalAward) {
                ctx.fillStyle = PRINCIPAL_AWARD_COLOR.hex;
                ctx.font = 'bold 18px Inter, sans-serif';
                ctx.fillText(`- ${t('principals_award')} -`, canvas.width / 2, yPos);
            }
            yPos += 40;

            // 7. Personal Note
            if (kudo.note && includeNote) {
                ctx.fillStyle = '#4B5563';
                ctx.font = 'italic 20px Inter, sans-serif';
                wrapText(ctx, `"${kudo.note}"`, canvas.width / 2, yPos, canvas.width - padding * 2, 24);
            }

            // 8. Footer
            ctx.fillStyle = '#6B7280';
            ctx.font = '18px Inter, sans-serif';
            ctx.fillText(isBrainBadge ? 'EduGravity Independent Thinker Initiative • Certified Brain Power' : `${t('awarded_by')} ${kudo.senderName}`, canvas.width / 2, canvas.height - 80);
            ctx.font = '14px Inter, sans-serif';
            ctx.fillText(new Date(kudo.timestamp).toLocaleDateString(), canvas.width / 2, canvas.height - 50);

            // Generate blob immediately to ensure it's ready for sharing
            try {
                canvas.toBlob((blob) => {
                    setShareBlob(blob);
                    setIsDrawing(false);
                }, 'image/png');
            } catch (blobErr) {
                console.warn("Canvas toBlob error:", blobErr);
                setIsDrawing(false);
            }
        };

        render();

    }, [kudo, student, org, includeNote, t]);

    const handleShare = async () => {
        if (!shareBlob) return;
        try {
            const file = new File([shareBlob], `${student.childName}-kudo.png`, { type: 'image/png' });
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({
                    title: `${t('kudos')} for ${student.childName}!`,
                    text: `So proud of ${student.childName} for earning the '${kudo.badgeName}' badge!`,
                    files: [file],
                });
            } else if (navigator.share) {
                await navigator.share({
                    title: `${t('kudos')} for ${student.childName}!`,
                    text: `So proud of ${student.childName} for earning the '${kudo.badgeName}' badge!`,
                });
            } else {
                handleDownload();
            }
        } catch (err: any) {
            // AbortError is thrown when user simply dismisses the native share dialog
            if (err?.name !== 'AbortError') {
                console.warn('Share not completed via system dialog, falling back to download:', err);
                handleDownload();
            }
        }
    };

    const handleDownload = () => {
        if (!shareBlob) return;
        const link = document.createElement('a');
        link.download = `${student.childName}-kudo.png`;
        link.href = URL.createObjectURL(shareBlob);
        link.click();
        URL.revokeObjectURL(link.href);
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col no-scrollbar" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{t('share_kudo')}</h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round">close</span></button>
                </div>
                <div className="flex-1 overflow-y-auto mb-4 relative">
                    <canvas ref={canvasRef} width="600" height="800" className="w-full h-auto border rounded-lg"></canvas>
                    <button
                        onClick={handleDownload}
                        disabled={isDrawing}
                        className="absolute top-4 right-4 bg-white/70 backdrop-blur-sm p-2 rounded-full text-gray-700 hover:bg-white disabled:opacity-50 transition-all"
                        title={t('download_certificate')}
                    >
                        <span className="material-icons-round">download</span>
                    </button>
                </div>
                {kudo.note && (
                    <label className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <input type="checkbox" checked={includeNote} onChange={() => setIncludeNote(!includeNote)} />
                        {t('include_note')}
                    </label>
                )}
                 <div className="flex gap-4 mt-auto">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-200 rounded-lg">{t('cancel')}</button>
                    <button 
                        onClick={canShare ? handleShare : handleDownload} 
                        disabled={isDrawing || (!canShare && !shareBlob)}
                        className="flex-1 py-3 bg-indigo-600 text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                         <span className={`material-icons-round ${isDrawing ? 'animate-spin' : ''}`}>{isDrawing ? 'sync' : (canShare ? 'share' : 'download')}</span>
                         {isDrawing ? t('preparing') : (canShare ? t('share') : t('download_certificate'))}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const KudosCornerView = ({ student, kudos, org, t, allHomeworks, studentHomeworkStatuses, tutorLogs }: {
    student: StudentConnection;
    kudos: Kudo[];
    org: Organization | undefined;
    t: (key: string, reps?: any) => string;
    allHomeworks?: Homework[];
    studentHomeworkStatuses?: StudentHomeworkStatus[];
    tutorLogs?: TutorLog[];
}) => {
    const [sharingKudo, setSharingKudo] = useState<Kudo | null>(null);
    const badges = kudos.filter(k => k.type === 'badge');

    const brainProfile = useMemo(() => {
        return computeStudentBrainPowerProfile(
            student.id,
            allHomeworks || [],
            studentHomeworkStatuses || [],
            tutorLogs || []
        );
    }, [student.id, allHomeworks, studentHomeworkStatuses, tutorLogs]);

    const handleShareBrainBadge = (badge: BrainPowerBadge) => {
        const virtualKudo: Kudo = {
            id: `brain-${badge.id}-${student.id}`,
            senderId: 'system',
            senderName: 'EduGravity Academic Council',
            receiverId: student.id,
            type: 'badge',
            badgeId: badge.id,
            reactions: {},
            timestamp: new Date().toISOString(),
            academicYearId: '',
            note: `Earned for outstanding independent thinking: ${badge.explicitSubtitle}. Completed homework using independent problem-solving skills.`
        };
        setSharingKudo(virtualKudo);
    };
    
    return (
        <>
            <div className="p-4 space-y-5">
                {/* 1. BRAIN POWER LEAGUE & INDEPENDENT THINKER HERO */}
                <div className="bg-gradient-to-br from-emerald-50/70 via-white to-amber-50/60 rounded-2xl shadow-xs border border-emerald-200/80 p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white flex items-center justify-center shadow-xs">
                                <span className="material-icons-round text-2xl">psychology</span>
                            </div>
                            <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h3 className="font-bold text-lg text-gray-900">Independent Thinker League</h3>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 border border-emerald-200 px-2.5 py-0.5 rounded-full">
                                        More Brain, Less AI
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-0.5">
                                    Rewarding students who solve homework with their own brain power and use less AI.
                                </p>
                            </div>
                        </div>

                        {/* Current Tier Indicator */}
                        <div className="bg-white px-3.5 py-2 rounded-xl border border-emerald-100 shadow-2xs flex items-center gap-2 self-start sm:self-auto">
                            <span className="text-2xl">
                                {brainProfile.currentTier === 'platinum' ? '💎' :
                                 brainProfile.currentTier === 'gold' ? '🥇' :
                                 brainProfile.currentTier === 'silver' ? '🥈' :
                                 brainProfile.currentTier === 'bronze' ? '🥉' : '🌱'}
                            </span>
                            <div>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block">Current Rank</span>
                                <span className="text-xs font-black text-gray-900 capitalize">
                                    {brainProfile.currentTier === 'none' ? 'Aspirant Thinker' : `${brainProfile.currentTier} Tier`}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Streak & Next Goal Banner */}
                    <div className="bg-white/80 p-3.5 rounded-xl border border-emerald-100 space-y-2.5">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-1.5">
                            <div className="flex items-center gap-2">
                                <span className="material-icons-round text-emerald-600 text-base">local_fire_department</span>
                                <span className="font-bold text-gray-800">
                                    {brainProfile.currentPureBrainStreak > 0 
                                        ? `${brainProfile.currentPureBrainStreak}-Day Pure Brain Streak (0 AI Used!)` 
                                        : 'Start your 100% Brain Power streak today!'}
                                </span>
                            </div>
                            <span className="text-indigo-600 font-semibold text-[11px]">
                                Next: {brainProfile.nextMilestoneDescription}
                            </span>
                        </div>

                        {/* 4-Tier Progression Ladder */}
                        <div className="grid grid-cols-4 gap-2 pt-1">
                            {[
                                { tier: 'bronze', label: 'Bronze', req: '1 Day', icon: '🥉' },
                                { tier: 'silver', label: 'Silver', req: '3 Days', icon: '🥈' },
                                { tier: 'gold', label: 'Gold', req: '7 Days', icon: '🥇' },
                                { tier: 'platinum', label: 'Platinum', req: '30 Days', icon: '💎' },
                            ].map((step, idx) => {
                                const tiersOrder = ['bronze', 'silver', 'gold', 'platinum'];
                                const currentTierIdx = tiersOrder.indexOf(brainProfile.currentTier);
                                const isUnlocked = currentTierIdx >= idx;
                                const isNext = currentTierIdx + 1 === idx;

                                return (
                                    <div 
                                        key={step.tier} 
                                        className={`p-2 rounded-xl text-center border transition-all ${
                                            isUnlocked 
                                                ? 'bg-emerald-50/80 border-emerald-300 text-emerald-900' 
                                                : isNext 
                                                    ? 'bg-amber-50/60 border-amber-300 text-amber-900 ring-1 ring-amber-300' 
                                                    : 'bg-gray-50 border-gray-200 text-gray-400'
                                        }`}
                                    >
                                        <div className="text-lg leading-none mb-1">{step.icon}</div>
                                        <p className="text-[11px] font-bold leading-tight truncate">{step.label}</p>
                                        <span className="text-[10px] opacity-80 block truncate">{step.req}</span>
                                        {isUnlocked && (
                                            <span className="material-icons-round !text-xs text-emerald-600 mt-0.5">verified</span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Stats Triplet */}
                    <div className="grid grid-cols-3 gap-2.5 text-center">
                        <div className="bg-white p-2.5 rounded-xl border border-gray-200 shadow-2xs">
                            <span className="text-base sm:text-lg font-black text-gray-900 block">{brainProfile.pureBrainTasks}</span>
                            <span className="text-[10px] sm:text-xs text-gray-500 font-medium">0 AI Tasks</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-gray-200 shadow-2xs">
                            <span className="text-base sm:text-lg font-black text-emerald-700 block">{brainProfile.pureBrainDays}</span>
                            <span className="text-[10px] sm:text-xs text-gray-500 font-medium">100% Brain Days</span>
                        </div>
                        <div className="bg-white p-2.5 rounded-xl border border-gray-200 shadow-2xs">
                            <span className="text-base sm:text-lg font-black text-indigo-700 block">{brainProfile.averageLifetimeScore}%</span>
                            <span className="text-[10px] sm:text-xs text-gray-500 font-medium">Brain Score Avg</span>
                        </div>
                    </div>

                    {/* Tier Badges Carousel / Grid */}
                    <div className="space-y-2 pt-1">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center justify-between">
                            <span>Independent Thinker Badges</span>
                            <span className="text-[11px] text-emerald-700 font-semibold normal-case">
                                {brainProfile.unlockedBadges.length} of {BRAIN_POWER_BADGES.length} Unlocked
                            </span>
                        </h4>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {BRAIN_POWER_BADGES.map(badge => {
                                const isEarned = brainProfile.unlockedBadges.some(b => b.id === badge.id);

                                return (
                                    <div 
                                        key={badge.id}
                                        className={`p-3.5 rounded-xl border transition-all flex flex-col justify-between space-y-3 ${
                                            isEarned 
                                                ? 'bg-white border-amber-300 shadow-xs ring-1 ring-amber-200' 
                                                : 'bg-gray-50/70 border-gray-200 opacity-60'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                                isEarned 
                                                    ? 'bg-gradient-to-tr from-amber-400 to-yellow-300 text-amber-950 shadow-2xs' 
                                                    : 'bg-gray-200 text-gray-400'
                                            }`}>
                                                <span className="material-icons-round text-2xl">{badge.icon}</span>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <h5 className="font-bold text-sm text-gray-900 truncate">{badge.name}</h5>
                                                    {isEarned && (
                                                        <span className="material-icons-round !text-xs text-amber-600" title="Unlocked">
                                                            verified
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[10px] font-bold text-emerald-700 mt-0.5">
                                                    {badge.explicitSubtitle}
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                                                    {badge.description}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                                                isEarned ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-600'
                                            }`}>
                                                {isEarned ? 'Unlocked' : 'In Progress'}
                                            </span>
                                            {isEarned && (
                                                <button
                                                    onClick={() => handleShareBrainBadge(badge)}
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:underline cursor-pointer"
                                                >
                                                    <span className="material-icons-round !text-sm">workspace_premium</span>
                                                    Share Certificate
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* 2. SCHOOL & TEACHER AWARDS */}
                <div className="bg-white rounded-xl shadow-sm border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="font-bold text-base text-gray-800">{t('kudos_corner')}</h3>
                            <p className="text-xs text-gray-500">{student.childName}'s Teacher & School Badges</p>
                        </div>
                        <span className="text-xs font-bold text-gray-400">{badges.length} Received</span>
                    </div>

                    {badges.length === 0 ? (
                         <div className="text-center p-6 text-gray-500 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                            <span className="material-icons-round text-4xl opacity-40 mb-2">military_tech</span>
                            <h4 className="font-bold text-sm text-gray-700">No Teacher Badges Yet</h4>
                            <p className="text-xs text-gray-400">Badges awarded by teachers and principals will appear here.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {badges.map(kudo => {
                                const badgeInfo = KUDO_BADGES.find(b => b.id === kudo.badgeId);
                                if (!badgeInfo) return null;

                                const color = kudo.isPrincipalAward 
                                    ? PRINCIPAL_AWARD_COLOR.text 
                                    : KUDO_CATEGORY_COLORS[badgeInfo.category]?.text || 'text-indigo-500';

                                return (
                                    <div key={kudo.id} className="bg-white rounded-xl shadow-2xs border border-gray-200 p-3.5 flex flex-col items-center text-center justify-between">
                                        <div className={`text-5xl ${color}`}>
                                            <span className="material-icons-round !text-6xl">{badgeInfo.icon}</span>
                                        </div>
                                        <div className="mt-2">
                                            <p className="font-bold text-sm text-gray-900">{badgeInfo.name}</p>
                                            <p className="text-[11px] text-gray-500 mt-0.5">by {kudo.senderName}</p>
                                            {kudo.isPrincipalAward && (
                                                <p className="text-[10px] font-bold text-amber-600 mt-1">{t('principals_award')}</p>
                                            )}
                                        </div>
                                        <button 
                                            onClick={() => setSharingKudo(kudo)} 
                                            className="mt-3 bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 text-xs font-bold rounded-full transition-colors"
                                        >
                                            {t('share')}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {sharingKudo && (
                <ShareKudoModal 
                    kudo={sharingKudo}
                    student={student}
                    org={org}
                    onClose={() => setSharingKudo(null)}
                    t={t}
                />
            )}
        </>
    );
};

export const ParentKudosView = ({ student, receivedKudos, sentKudos, org, t, allHomeworks, studentHomeworkStatuses, tutorLogs }: {
    student: StudentConnection;
    receivedKudos: Kudo[];
    sentKudos: Kudo[];
    org: Organization | undefined;
    t: (key: string, reps?: any) => string;
    allHomeworks?: Homework[];
    studentHomeworkStatuses?: StudentHomeworkStatus[];
    tutorLogs?: TutorLog[];
}) => {
    const [activeTab, setActiveTab] = useState<'received' | 'given'>('received');

    return (
        <div className="space-y-4">
             <div className="flex bg-white rounded-xl shadow-sm p-1 border mx-4 mt-4">
                <button 
                    onClick={() => setActiveTab('received')} 
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${activeTab === 'received' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Received
                </button>
                <button 
                    onClick={() => setActiveTab('given')} 
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${activeTab === 'given' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Given
                </button>
            </div>

            {activeTab === 'received' ? (
                <KudosCornerView 
                    student={student} 
                    kudos={receivedKudos} 
                    org={org} 
                    t={t} 
                    allHomeworks={allHomeworks}
                    studentHomeworkStatuses={studentHomeworkStatuses}
                    tutorLogs={tutorLogs}
                />
            ) : (
                <div className="p-4 space-y-3">
                    {sentKudos.length === 0 ? (
                        <div className="text-center p-8 text-gray-500 bg-white rounded-xl shadow-sm border">
                            <span className="material-icons-round text-5xl opacity-50 mb-4">volunteer_activism</span>
                            <p>You haven't sent any appreciation to teachers yet.</p>
                        </div>
                    ) : (
                        sentKudos.map(kudo => (
                             <div key={kudo.id} className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="bg-indigo-50 p-2 rounded-full">
                                         <span className="material-icons-round text-indigo-500 text-2xl">
                                            {TEACHER_KUDO_BADGES.find(b => b.id === kudo.badgeId)?.icon || 'favorite'}
                                         </span>
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm">
                                            <span className="text-gray-600">You sent </span>
                                            <span className="font-bold text-indigo-600">{kudo.badgeName}</span>
                                            <span className="text-gray-600"> to </span>
                                            <span className="font-bold">Teacher</span>
                                        </p>
                                        {kudo.note && <p className="text-sm italic mt-1 text-gray-600">"{kudo.note}"</p>}
                                        <p className="text-xs text-gray-400 mt-1">{new Date(kudo.timestamp).toLocaleDateString()}</p>
                                    </div>
                                </div>
                             </div>
                        ))
                    )}
                </div>
            )}
        </div>
    )
};

const KudoLogItem: FC<{ kudo: Kudo, student: StudentConnection, t: (key: string, reps?: any) => string, userLanguage?: string }> = ({ kudo, student, t, userLanguage = 'en' }) => {
    const [translatedNote, setTranslatedNote] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        setTranslatedNote(null);
        setShowOriginal(false);
    }, [userLanguage, kudo.id, kudo.note]);

    const handleTranslateNote = async () => {
        if (!kudo.note) return;
        if (!translatedNote) {
            setIsTranslating(true);
            try {
                const result = await translateContent(kudo.note, userLanguage);
                setTranslatedNote(result);
            } catch (err) {
                console.error("Kudo translation error:", err);
            } finally {
                setIsTranslating(false);
            }
        }
        setShowOriginal(!showOriginal);
    };

    const badge = KUDO_BADGES.find(b => b.id === kudo.badgeId);
    const colors = badge ? (kudo.isPrincipalAward ? PRINCIPAL_AWARD_COLOR : KUDO_CATEGORY_COLORS[badge.category]) : { text: 'text-gray-500', border: 'border-gray-400' };
    const noteToShow = showOriginal ? kudo.note : (translatedNote || kudo.note);

    return (
        <div className={`bg-white p-3 rounded-lg border-l-4 ${colors.border}`}>
            <div className="flex items-start gap-3">
                <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                <div className="flex-1">
                    <p className="text-sm">
                        <span className="font-bold">{student.childName}</span>
                        <span className="text-gray-600"> received the </span>
                        <span className={`font-bold ${colors.text}`}>{kudo.badgeName || 'a reaction'}</span>
                        <span className="text-gray-600"> badge from </span>
                        <span className="font-bold">{kudo.senderName}.</span>
                    </p>
                    {kudo.isPrincipalAward && <p className="text-xs font-bold text-amber-600 mt-1">{t('principals_award')}</p>}
                    {kudo.note && (
                        <div className="mt-2 bg-gray-50 p-2 rounded">
                            <p className="text-sm italic">"{noteToShow}"</p>
                            <button 
                                onClick={handleTranslateNote}
                                disabled={isTranslating}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 mt-1 flex items-center gap-1 transition-colors"
                            >
                                <span className="material-icons-round text-[12px]">translate</span>
                                {isTranslating ? (t('processing_translation') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (t('see_original') || 'See Original'))}
                            </button>
                        </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        {kudo.timestamp.toLocaleString()}
                    </p>
                </div>
                {badge && (
                    <div className={`text-3xl ${colors.text} opacity-80`}>
                        <span className="material-icons-round !text-4xl">{badge.icon}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const TeacherKudoLogItem: FC<{ kudo: Kudo, t: (key: string, reps?: any) => string, userLanguage?: string }> = ({ kudo, t, userLanguage = 'en' }) => {
    const [translatedNote, setTranslatedNote] = useState<string | null>(null);
    const [showOriginal, setShowOriginal] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);

    useEffect(() => {
        setTranslatedNote(null);
        setShowOriginal(false);
    }, [userLanguage, kudo.id, kudo.note]);

    const handleTranslateNote = async () => {
        if (!kudo.note) return;
        if (!translatedNote) {
            setIsTranslating(true);
            try {
                const result = await translateContent(kudo.note, userLanguage);
                setTranslatedNote(result);
            } catch (err) {
                console.error("Teacher kudo translation error:", err);
            } finally {
                setIsTranslating(false);
            }
        }
        setShowOriginal(!showOriginal);
    };

    const badge = TEACHER_KUDO_BADGES.find(b => b.id === kudo.badgeId);
    const colors = badge ? KUDO_CATEGORY_COLORS[badge.category] : { text: 'text-gray-500', border: 'border-gray-400' };
    const noteToShow = showOriginal ? kudo.note : (translatedNote || kudo.note);

    return (
        <div className={`bg-white p-3 rounded-lg border-l-4 ${colors.border}`}>
            <div className="flex items-start gap-3">
                <div className={`text-3xl ${colors.text} opacity-80`}>
                    <span className="material-icons-round !text-4xl">{badge?.icon || 'favorite'}</span>
                </div>
                <div className="flex-1">
                    <p className="text-sm">
                        {kudo.senderRole === 'principal' ? (
                            <>
                                <span className="text-gray-600"> Received </span>
                                <span className={`font-bold ${colors.text}`}>{kudo.badgeName || 'Appreciation'}</span>
                                <span className="text-gray-600"> from </span>
                                <span className="font-bold">Principal {kudo.senderName}.</span>
                            </>
                        ) : (
                            <>
                                <span className="text-gray-600"> Received </span>
                                <span className={`font-bold ${colors.text}`}>{kudo.badgeName || 'Appreciation'}</span>
                                <span className="text-gray-600"> from </span>
                                <span className="font-bold">{kudo.senderName}.</span>
                            </>
                        )}
                    </p>
                    {kudo.note && (
                        <div className="mt-2 bg-gray-50 p-2 rounded">
                            <p className="text-sm italic">"{noteToShow}"</p>
                            <button 
                                onClick={handleTranslateNote}
                                disabled={isTranslating}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 mt-1 flex items-center gap-1 transition-colors"
                            >
                                <span className="material-icons-round text-[12px]">translate</span>
                                {isTranslating ? (t('processing_translation') || 'Translating...') : (showOriginal ? (t('see_translation') || 'See Translation') : (t('see_original') || 'See Original'))}
                            </button>
                        </div>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                        {kudo.timestamp.toLocaleString()}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const KudosLogView = ({ kudos, students, t, userLanguage = 'en' }: {
    kudos: Kudo[];
    students: StudentConnection[];
    t: (key: string, reps?: any) => string;
    userLanguage?: string;
}) => {
    const [viewMode, setViewMode] = useState<'feed' | 'students' | 'received'>('feed');

    const sortedKudos = [...kudos].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    
    // Separate student kudos from teacher kudos
    const studentKudos = sortedKudos.filter(k => !k.teacherId);
    const receivedKudos = sortedKudos.filter(k => !!k.teacherId);

    const kudosThisWeek = studentKudos.filter(k => {
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        return k.timestamp >= oneWeekAgo;
    }).length;

    const badgeCounts = studentKudos.reduce((acc, kudo) => {
        if (kudo.badgeId) {
            acc[kudo.badgeId] = (acc[kudo.badgeId] || 0) + 1;
        }
        return acc;
    }, {} as Record<string, number>);

    const topBadgeId = Object.keys(badgeCounts).sort((a, b) => badgeCounts[b] - badgeCounts[a])[0];
    const topBadge = KUDO_BADGES.find(b => b.id === topBadgeId);

    // Compute metrics per student
    const studentMetrics = students.map(student => {
        const sKudos = studentKudos.filter(k => k.studentId === student.id);
        const badgesCount = sKudos.filter(k => k.type === 'badge').length;
        const reactionsCount = sKudos.filter(k => k.type === 'reaction').length;
        return {
            student,
            total: sKudos.length,
            badges: badgesCount,
            reactions: reactionsCount
        };
    }).sort((a, b) => b.total - a.total);

    return (
        <div className="p-4 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border p-4">
                <h3 className="font-bold text-lg text-gray-800">{t('kudos_log')}</h3>
                <div className="grid grid-cols-2 gap-4 mt-2 text-center">
                    <div>
                        <p className="text-2xl font-bold">{studentKudos.length}</p>
                        <p className="text-xs text-gray-500">{t('total_kudos_given')}</p>
                    </div>
                     <div>
                        <p className="text-2xl font-bold">{kudosThisWeek}</p>
                        <p className="text-xs text-gray-500">{t('this_past_week')}</p>
                    </div>
                </div>
                {topBadge && (
                    <div className="mt-4 pt-3 border-t text-center">
                         <p className="text-xs text-gray-500">{t('most_awarded_badge')}</p>
                         <p className="font-bold">{topBadge.name}</p>
                    </div>
                )}
            </div>

            <div className="flex bg-white rounded-xl shadow-sm p-1 border overflow-x-auto">
                <button 
                    onClick={() => setViewMode('feed')} 
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${viewMode === 'feed' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Given to Students
                </button>
                <button 
                    onClick={() => setViewMode('students')} 
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${viewMode === 'students' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    By Student
                </button>
                <button 
                    onClick={() => setViewMode('received')} 
                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${viewMode === 'received' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                >
                    Received
                </button>
            </div>

            {viewMode === 'feed' && (
                <>
                    {studentKudos.length === 0 ? (
                        <div className="text-center p-8 text-gray-500 bg-white rounded-xl shadow-sm border">
                            <span className="material-icons-round text-5xl opacity-50 mb-4">military_tech</span>
                            <p>{t('no_kudos_yet')}</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {studentKudos.map(kudo => {
                                const student = students.find(s => s.id === kudo.studentId);
                                if (!student) return null;
                                return <KudoLogItem key={kudo.id} kudo={kudo} student={student} t={t} userLanguage={userLanguage} />;
                            })}
                        </div>
                    )}
                </>
            )}

            {viewMode === 'students' && (
                <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                    <div className="divide-y divide-gray-100">
                        {studentMetrics.map(({ student, total, badges, reactions }) => (
                            <div key={student.id} className={`p-4 flex items-center gap-3 ${total === 0 ? 'bg-orange-50' : ''}`}>
                                <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                <div className="flex-1">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="font-bold text-gray-800">{student.childName}</span>
                                        <div className="flex items-center gap-1 bg-indigo-50 px-2 py-0.5 rounded-full">
                                            <span className="text-sm font-bold text-indigo-700">{total}</span>
                                            <span className="material-icons-round text-xs text-indigo-400">military_tech</span>
                                        </div>
                                    </div>
                                    {total > 0 ? (
                                        <div className="flex gap-4 text-xs text-gray-500">
                                            <span className="flex items-center gap-1">
                                                <span className="material-icons-round text-[14px] text-amber-500">local_police</span>
                                                {badges} Badges
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <span className="material-icons-round text-[14px] text-blue-500">thumb_up</span>
                                                {reactions} Reactions
                                            </span>
                                        </div>
                                    ) : (
                                        <p className="text-xs text-orange-600 font-medium">No kudos yet. Encouragement needed!</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {viewMode === 'received' && (
                <>
                    {receivedKudos.length === 0 ? (
                        <div className="text-center p-8 text-gray-500 bg-white rounded-xl shadow-sm border">
                            <span className="material-icons-round text-5xl opacity-50 mb-4">favorite_border</span>
                            <p>No kudos received from families or principal yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {receivedKudos.map(kudo => (
                                <TeacherKudoLogItem key={kudo.id} kudo={kudo} t={t} userLanguage={userLanguage} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};


export const SendKudoModal = ({ isOpen, onClose, user, students, activeYear, onSendKudo, t }: {
    isOpen: boolean;
    onClose: () => void;
    user: UserProfile;
    students: StudentConnection[];
    activeYear: AcademicYear;
    onSendKudo: (kudo: Omit<Kudo, 'id'|'timestamp'>) => Promise<void>;
    t: (key: string, reps?: any) => string;
}) => {
    const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
    const [activeTab, setActiveTab] = useState<'badge' | 'reaction'>('badge');
    const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
    const [selectedReaction, setSelectedReaction] = useState<ReactionType | null>(null);
    const [note, setNote] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSelectedStudentIds([]);
            setSelectedBadge(null);
            setSelectedReaction(null);
            setNote('');
            setSearchTerm('');
            setIsProcessing(false);
        }
    }, [isOpen]);

    const filteredStudents = students.filter(s => s.childName.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const allFilteredSelected = filteredStudents.length > 0 && filteredStudents.every(s => selectedStudentIds.includes(s.id));

    const handleToggleSelectAll = () => {
        const filteredIds = filteredStudents.map(s => s.id);
        if (allFilteredSelected) {
            // Deselect all filtered students
            setSelectedStudentIds(prev => prev.filter(id => !filteredIds.includes(id)));
        } else {
            // Select all filtered students, preserving existing selections
            setSelectedStudentIds(prev => [...new Set([...prev, ...filteredIds])]);
        }
    };

    const handleSend = async () => {
        if (selectedStudentIds.length === 0 || (!selectedBadge && !selectedReaction)) return;
        
        for (const studentId of selectedStudentIds) {
            const student = students.find(s => s.id === studentId);
            if (!student) continue;
            
            const trimmedNote = note.trim();
            const baseKudo: Partial<Omit<Kudo, 'id' | 'timestamp' | 'type'>> = {
                studentId,
                classId: student.classId,
                academicYearId: activeYear.id,
                senderId: user.id,
                senderName: user.name,
                senderRole: user.role,
                isPrincipalAward: user.role === 'principal',
            };

            if (trimmedNote) {
                baseKudo.note = trimmedNote;
            }

            if (activeTab === 'badge' && selectedBadge) {
                const badgeInfo = KUDO_BADGES.find(b => b.id === selectedBadge);
                await onSendKudo({
                    ...baseKudo,
                    type: 'badge',
                    badgeId: selectedBadge,
                    badgeName: badgeInfo?.name
                } as Omit<Kudo, 'id' | 'timestamp'>);
            } else if (activeTab === 'reaction' && selectedReaction) {
                 await onSendKudo({
                    ...baseKudo,
                    type: 'reaction',
                    reactionType: selectedReaction
                } as Omit<Kudo, 'id' | 'timestamp'>);
            }
        }
        onClose();
    };

    if (!isOpen) return null;
    
    const categories: KudoBadge['category'][] = [
        'academic', 'behavioral', 'character', 'communication', 'community', 'arts_creativity', 'sports'
    ];

    const badgeGroups = categories.map(category => {
        return {
            title: category.replace('_', ' & ').replace(/\b\w/g, l => l.toUpperCase()),
            badges: KUDO_BADGES.filter(b => b.category === category)
        }
    }).filter(group => group.badges.length > 0);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">{t('send_kudo')}</h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round">close</span></button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    <div>
                        <h3 className="font-bold text-md mb-2">{t('select_students')}</h3>
                        <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t('search_student_by_name')} className="w-full p-2 border border-gray-200 rounded-lg mb-2" />
                        <div className="max-h-48 overflow-y-auto space-y-2 border p-2 rounded-lg">
                            {filteredStudents.length > 0 && (
                                <label className="flex items-center gap-3 p-2 bg-gray-100 rounded-lg cursor-pointer font-bold border-b sticky top-0 z-10">
                                    <input
                                        type="checkbox"
                                        className="w-5 h-5"
                                        checked={allFilteredSelected}
                                        onChange={handleToggleSelectAll}
                                        aria-label={allFilteredSelected ? t('deselect_all') : t('select_all')}
                                    />
                                    <span>{allFilteredSelected ? t('deselect_all') : t('select_all')}</span>
                                </label>
                            )}
                            {filteredStudents.map(student => (
                                <label key={student.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg cursor-pointer">
                                    <input type="checkbox" className="w-5 h-5" checked={selectedStudentIds.includes(student.id)} onChange={() => setSelectedStudentIds(prev => prev.includes(student.id) ? prev.filter(id => id !== student.id) : [...prev, student.id])} />
                                    <Avatar name={student.childName} photoUrl={student.photoUrl} size="sm" />
                                    <span>{student.childName}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                        <button onClick={() => setActiveTab('badge')} className={`flex-1 rounded-md py-2 font-bold ${activeTab === 'badge' ? 'bg-white shadow' : ''}`}>{t('badges')}</button>
                        <button onClick={() => setActiveTab('reaction')} className={`flex-1 rounded-md py-2 font-bold ${activeTab === 'reaction' ? 'bg-white shadow' : ''}`}>{t('reactions')}</button>
                    </div>

                    {activeTab === 'badge' && (
                        <div className="space-y-3">
                            {badgeGroups.map(group => (
                                <div key={group.title}>
                                    <h4 className="font-bold text-sm text-gray-500 uppercase mb-2">{group.title}</h4>
                                    <div className="grid grid-cols-3 gap-2">
                                        {group.badges.map(badge => (
                                            <button key={badge.id} onClick={() => setSelectedBadge(badge.id)} className={`p-2 border rounded-lg text-center ${selectedBadge === badge.id ? 'bg-indigo-50 border-indigo-500' : 'hover:bg-gray-50'}`}>
                                                <span className="material-icons-round text-3xl">{badge.icon}</span>
                                                <p className="text-xs font-medium mt-1">{badge.name}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                     {activeTab === 'reaction' && (
                         <div className="grid grid-cols-4 gap-2">
                            {REACTIONS.map(reaction => (
                                <button
                                    key={reaction.id}
                                    onClick={() => setSelectedReaction(reaction.id)}
                                    className={`p-2 border rounded-lg text-center ${selectedReaction === reaction.id ? 'bg-indigo-50 border-indigo-500' : 'hover:bg-gray-50'}`}
                                >
                                    <span className="material-icons-round text-3xl">{reaction.icon}</span>
                                    <p className="text-xs font-medium mt-1">{reaction.name}</p>
                                </button>
                            ))}
                        </div>
                     )}

                    <div className="relative w-full">
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={2}
                            placeholder={t('add_personal_note')}
                            className="w-full p-2 pr-14 border-2 border-gray-200 rounded-lg"
                            disabled={isProcessing}
                            maxLength={300}
                        />
                         <VoiceInput
                            onTranscriptReceived={(transcript) => setNote(prev => `${prev} ${transcript}`.trim())}
                            onProcessingChange={setIsProcessing}
                            className="absolute top-3 right-3"
                        />
                    </div>
                </div>
                 <div className="flex gap-4 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-200 rounded-lg">{t('cancel')}</button>
                    <button onClick={handleSend} disabled={selectedStudentIds.length === 0 || (!selectedBadge && !selectedReaction) || isProcessing} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg disabled:opacity-50">{t('send')}</button>
                </div>
            </div>
        </div>
    );
};

export const SendTeacherKudoModal = ({ isOpen, onClose, user, teachers, student, activeYear, onSendKudo, t }: {
    isOpen: boolean;
    onClose: () => void;
    user: UserProfile;
    teachers: UserProfile[];
    student: StudentConnection;
    activeYear: AcademicYear;
    onSendKudo: (kudo: Omit<Kudo, 'id'|'timestamp'>) => Promise<void>;
    t: (key: string, reps?: any) => string;
}) => {
    const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
    const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSelectedTeacherIds([]);
            setSelectedBadge(null);
            setNote('');
            setIsProcessing(false);
        }
    }, [isOpen]);

    const handleSend = async () => {
        if (selectedTeacherIds.length === 0 || !selectedBadge) return;
        setIsProcessing(true);
        
        for (const teacherId of selectedTeacherIds) {
            const trimmedNote = note.trim();
            const badgeInfo = TEACHER_KUDO_BADGES.find(b => b.id === selectedBadge);
            const teacher = teachers.find(t => t.id === teacherId);
            
            const kudoData: Omit<Kudo, 'id' | 'timestamp'> = {
                studentId: student.id, // For reference of which family sent it
                teacherId: teacherId, // KEY FIELD for Teacher Kudo
                teacherName: teacher?.name,
                classId: student.classId,
                academicYearId: activeYear.id,
                senderId: user.id,
                senderName: `${user.name} (Parent of ${student.childName})`,
                senderRole: 'parent',
                isPrincipalAward: false,
                type: 'badge',
                badgeId: selectedBadge,
                badgeName: badgeInfo?.name,
                note: trimmedNote || undefined
            };

            await onSendKudo(kudoData);
        }
        setIsProcessing(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Send Appreciation</h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round">close</span></button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    <div>
                        <h3 className="font-bold text-md mb-2">Select Teacher(s)</h3>
                        <div className="max-h-48 overflow-y-auto space-y-2 border p-2 rounded-lg">
                            {teachers.map(teacher => (
                                <label key={teacher.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="w-5 h-5" 
                                        checked={selectedTeacherIds.includes(teacher.id)} 
                                        onChange={() => setSelectedTeacherIds(prev => prev.includes(teacher.id) ? prev.filter(id => id !== teacher.id) : [...prev, teacher.id])} 
                                    />
                                    <Avatar name={teacher.name} photoUrl={teacher.photoUrl} size="sm" />
                                    <span>{teacher.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold text-md mb-2">Select Badge</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {TEACHER_KUDO_BADGES.map(badge => (
                                <button key={badge.id} onClick={() => setSelectedBadge(badge.id)} className={`p-2 border rounded-lg text-left flex items-center gap-2 ${selectedBadge === badge.id ? 'bg-indigo-50 border-indigo-500' : 'hover:bg-gray-50'}`}>
                                    <span className="material-icons-round text-2xl text-indigo-500">{badge.icon}</span>
                                    <span className="text-sm font-medium">{badge.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="relative w-full">
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={3}
                            placeholder="Add a note of appreciation (optional)..."
                            className="w-full p-2 pr-14 border-2 border-gray-200 rounded-lg"
                            disabled={isProcessing}
                            maxLength={300}
                        />
                         <VoiceInput
                            onTranscriptReceived={(transcript) => setNote(prev => `${prev} ${transcript}`.trim())}
                            onProcessingChange={setIsProcessing}
                            className="absolute top-3 right-3"
                        />
                    </div>
                </div>
                 <div className="flex gap-4 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-200 rounded-lg">{t('cancel')}</button>
                    <button onClick={handleSend} disabled={selectedTeacherIds.length === 0 || !selectedBadge || isProcessing} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg disabled:opacity-50">{t('send')}</button>
                </div>
            </div>
        </div>
    );
};

export const SendPrincipalToTeacherKudoModal = ({ isOpen, onClose, user, teachers, activeYear, onSendKudo, t }: {
    isOpen: boolean;
    onClose: () => void;
    user: UserProfile;
    teachers: UserProfile[];
    activeYear: AcademicYear;
    onSendKudo: (kudo: Omit<Kudo, 'id'|'timestamp'>) => Promise<void>;
    t: (key: string, reps?: any) => string;
}) => {
    const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
    const [selectedBadge, setSelectedBadge] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setSelectedTeacherIds([]);
            setSelectedBadge(null);
            setNote('');
            setIsProcessing(false);
        }
    }, [isOpen]);

    const handleSend = async () => {
        if (selectedTeacherIds.length === 0 || !selectedBadge) return;
        setIsProcessing(true);
        
        for (const teacherId of selectedTeacherIds) {
            const trimmedNote = note.trim();
            const badgeInfo = TEACHER_KUDO_BADGES.find(b => b.id === selectedBadge);
            const teacher = teachers.find(t => t.id === teacherId);
            
            const kudoData: Omit<Kudo, 'id' | 'timestamp'> = {
                teacherId: teacherId,
                teacherName: teacher?.name,
                classId: 'GLOBAL', // Principal to teacher isn't specific to one class, but should show globally
                academicYearId: activeYear.id,
                senderId: user.id,
                senderName: user.name,
                senderRole: 'principal',
                isPrincipalAward: true,
                type: 'badge',
                badgeId: selectedBadge,
                badgeName: badgeInfo?.name,
                note: trimmedNote || undefined
            };

            await onSendKudo(kudoData);
        }
        setIsProcessing(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold text-gray-800">Appreciate Teachers</h2>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:bg-gray-100 rounded-full"><span className="material-icons-round">close</span></button>
                </div>
                
                <div className="flex-1 overflow-y-auto space-y-4">
                    <div>
                        <h3 className="font-bold text-md mb-2">Select Teacher(s)</h3>
                        <div className="max-h-48 overflow-y-auto space-y-2 border p-2 rounded-lg">
                            {teachers.map(teacher => (
                                <label key={teacher.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg cursor-pointer">
                                    <input 
                                        type="checkbox" 
                                        className="w-5 h-5" 
                                        checked={selectedTeacherIds.includes(teacher.id)} 
                                        onChange={() => setSelectedTeacherIds(prev => prev.includes(teacher.id) ? prev.filter(id => id !== teacher.id) : [...prev, teacher.id])} 
                                    />
                                    <Avatar name={teacher.name} photoUrl={teacher.photoUrl} size="sm" />
                                    <span>{teacher.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h3 className="font-bold text-md mb-2">Select Badge</h3>
                        <div className="grid grid-cols-2 gap-2">
                            {TEACHER_KUDO_BADGES.map(badge => (
                                <button key={badge.id} onClick={() => setSelectedBadge(badge.id)} className={`p-2 border rounded-lg text-left flex items-center gap-2 ${selectedBadge === badge.id ? 'bg-indigo-50 border-indigo-500' : 'hover:bg-gray-50'}`}>
                                    <span className="material-icons-round text-2xl text-indigo-500">{badge.icon}</span>
                                    <span className="text-sm font-medium">{badge.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="relative w-full">
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            rows={3}
                            placeholder="Add a note of appreciation (optional)..."
                            className="w-full p-2 pr-14 border-2 border-gray-200 rounded-lg"
                            disabled={isProcessing}
                            maxLength={300}
                        />
                         <VoiceInput
                            onTranscriptReceived={(transcript) => setNote(prev => `${prev} ${transcript}`.trim())}
                            onProcessingChange={setIsProcessing}
                            className="absolute top-3 right-3"
                        />
                    </div>
                </div>
                 <div className="flex gap-4 mt-6">
                    <button onClick={onClose} className="flex-1 py-3 bg-gray-200 rounded-lg">{t('cancel')}</button>
                    <button onClick={handleSend} disabled={selectedTeacherIds.length === 0 || !selectedBadge || isProcessing} className="flex-1 py-3 bg-indigo-600 text-white rounded-lg disabled:opacity-50">{t('send')}</button>
                </div>
            </div>
        </div>
    );
};

export const PrincipalKudosLog = ({ onBack, kudos, students, classes, teachers, user, t }: {
    onBack: () => void;
    kudos: Kudo[];
    students: StudentConnection[];
    classes: ClassRoom[];
    teachers: UserProfile[];
    user: UserProfile;
    t: (key: string, reps?: any) => string;
}) => {
    const [activeTab, setActiveTab] = useState<'students' | 'teachers' | 'given'>('students');
    const [selectedClassId, setSelectedClassId] = useState<string>('');

    // Filter logic
    const studentKudos = kudos.filter(k => !k.teacherId);
    const teacherKudos = kudos.filter(k => !!k.teacherId && k.senderRole === 'parent');
    const givenKudos = kudos.filter(k => k.senderId === user.id);

    const filteredStudentKudos = selectedClassId 
        ? studentKudos.filter(k => k.classId === selectedClassId)
        : studentKudos;

    return (
        <div className="flex flex-col h-full bg-gray-50">
             <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-2">
                <button onClick={onBack} className="p-2 text-gray-500 hover:bg-gray-100 rounded-full">
                    <span className="material-icons-round">arrow_back</span>
                </button>
                <h2 className="text-lg font-bold text-gray-800">{t('kudos_log')}</h2>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
                <div className="flex bg-white rounded-xl shadow-sm p-1 border mb-4 overflow-x-auto">
                    <button 
                        onClick={() => setActiveTab('students')} 
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${activeTab === 'students' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Student Recognition
                    </button>
                    <button 
                        onClick={() => setActiveTab('teachers')} 
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${activeTab === 'teachers' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Parent {'->'} Teacher
                    </button>
                    <button 
                        onClick={() => setActiveTab('given')} 
                        className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors whitespace-nowrap px-2 ${activeTab === 'given' ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Given by Me
                    </button>
                </div>

                {activeTab === 'students' && (
                    <div className="space-y-4">
                        <select 
                            value={selectedClassId} 
                            onChange={e => setSelectedClassId(e.target.value)}
                            className="w-full p-2 border border-gray-200 rounded-lg bg-white"
                        >
                            <option value="">All Classes</option>
                            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>

                        {filteredStudentKudos.length === 0 ? (
                             <div className="text-center p-8 text-gray-500">
                                <p>No kudos found.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredStudentKudos.sort((a,b) => b.timestamp.getTime() - a.timestamp.getTime()).map(kudo => {
                                    const student = students.find(s => s.id === kudo.studentId);
                                    if (!student) return null;
                                    return <KudoLogItem key={kudo.id} kudo={kudo} student={student} t={t} />;
                                })}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'teachers' && (
                    <div className="space-y-4">
                         {teacherKudos.length === 0 ? (
                             <div className="text-center p-8 text-gray-500">
                                <p>No teacher appreciation kudos found.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {teacherKudos.sort((a,b) => b.timestamp.getTime() - a.timestamp.getTime()).map(kudo => (
                                    <TeacherKudoLogItem key={kudo.id} kudo={kudo} t={t} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'given' && (
                    <div className="space-y-4">
                         {givenKudos.length === 0 ? (
                             <div className="text-center p-8 text-gray-500">
                                <p>You haven't given any kudos yet.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {givenKudos.sort((a,b) => b.timestamp.getTime() - a.timestamp.getTime()).map(kudo => {
                                    if (kudo.teacherId) {
                                        return <TeacherKudoLogItem key={kudo.id} kudo={kudo} t={t} />;
                                    } else {
                                        const student = students.find(s => s.id === kudo.studentId);
                                        if (!student) return null;
                                        return <KudoLogItem key={kudo.id} kudo={kudo} student={student} t={t} />;
                                    }
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
